
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { BoPanel } from 'bonomo';
import { namespace } from 'vuex-class';
import axios from 'axios';
import { settings } from '@/plugins/settings';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import CreateButton from '@/components/shared/CreateButton.vue';
import { ICommunityContact } from '@/model/Community/communityInterfaces';
import { IAuthUserData } from '@/store/modules/authModule';
import { EPermissionActions } from '@/model/EPermissionActions';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';

const authModule = namespace('authModule');

@Component({
  name: 'CommunityContacts',
  components: { BoPanel, CommunityNav, CreateButton },
})
export default class CommunityContacts extends Vue {
  @Prop()
  protected communityId!: number;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected loading: boolean = true;
  protected contacts: ICommunityContact[] = [];

  protected createDialog: boolean = false;
  protected creating: boolean = false;
  protected createError: boolean = false;
  protected newContact: { user_email: string | null; role: string } = { user_email: null, role: '' };

  protected deleteDialog: boolean = false;
  protected deletingId: number | null = null;
  protected contactToDelete: ICommunityContact | null = null;

  get canCreate(): boolean {
    return this.$canAction(EPermissionActions.ACTION_COMMUNITIES_UPDATE);
  }

  get headers(): BetterDataTableHeader[] {
    const cols: BetterDataTableHeader[] = [
      { text: this.$t('communities.contacts.email') as string,       value: 'user_email',   sortable: false },
      { text: this.$t('communities.contacts.role') as string,        value: 'role',         sortable: false },
    ];
    if (this.canCreate) {
      cols.push({ text: '', value: 'actions', sortable: false, align: 'end' });
    }
    return cols;
  }

  get contactEmail(): string | null {
    return this.isValidEmail(this.newContact.user_email) ? (this.newContact.user_email as string).trim() : null;
  }

  get contactRoleOptions() {
    return [
      { text: this.$t('communities.contacts.roles.asset_manager'),     value: 'asset_manager' },
      { text: this.$t('communities.contacts.roles.facility_manager'),  value: 'facility_manager' },
      { text: this.$t('communities.contacts.roles.marketing_manager'), value: 'marketing_manager' },
      { text: this.$t('communities.contacts.roles.property_manager'), value: 'property_manager' },
    ];
  }

  isValidEmail(email: string | null): boolean {
    return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  emailRule(email: string | null): boolean | string {
    return !email || this.isValidEmail(email) || (this.$t('communities.contacts.invalidEmail') as string);
  }

  async loadData() {
    this.loading = true;
    try {
      const contactsRes = await axios.get(settings.endpoint + 'communities/' + this.communityId + '/contacts');
      this.contacts = contactsRes.data.data || contactsRes.data;
    } finally {
      this.loading = false;
    }
  }

  async submitCreate() {
    if (!this.contactEmail || !this.newContact.role) return;
    this.creating = true;
    this.createError = false;
    try {
      await axios.post(
        settings.endpoint + 'communities/' + this.communityId + '/contacts',
        { user_email: this.contactEmail, role: this.newContact.role }
      );
      this.closeDialog();
      this.$uiBus.emit('success', this.$t('communities.contacts.addedSuccess'));
      await this.loadData();
    } catch (error) {
      this.createError = true;
      this.notifyError(error);
    } finally {
      this.creating = false;
    }
  }

  notifyError(error: any) {
    const errData = error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0];
    const msg = (errData && errData.title) || this.$t('common.errorHappened');
    this.$uiBus.emit('alert', this.$t('common.error'), msg);
  }

  closeDialog() {
    this.createDialog = false;
    this.newContact = { user_email: null, role: '' };
    this.createError = false;
  }

  confirmDelete(contact: ICommunityContact) {
    this.contactToDelete = contact;
    this.deleteDialog = true;
  }

  async submitDelete() {
    if (!this.contactToDelete) return;
    this.deletingId = this.contactToDelete.id;
    try {
      await axios.delete(
        settings.endpoint + 'communities/' + this.communityId + '/contacts/' + this.contactToDelete.id
      );
      this.deleteDialog = false;
      this.contactToDelete = null;
      this.$uiBus.emit('success', this.$t('common.deleted'));
      await this.loadData();
    } catch (error) {
      this.notifyError(error);
    } finally {
      this.deletingId = null;
    }
  }
}
